import React, { useCallback, memo, useState, useRef } from 'react';
import {
  Bold,
  Italic,
  Strikethrough,
  Eraser,
  Type,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Heading,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Heading5,
  Heading6,
  List,
  ListOrdered,
  FileCode,
  Quote,
  Minus,
  VideoIcon,
  ImageIcon,
  Undo,
  Redo,
  Check,
  Link as LinkIcon,
  Palette,
  Mic,
  Loader2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import AiMenu from './AiMenu';
import { useAi } from '../providers/AiProvider';

const ToolbarButton = memo(({ active, onClick, title, children, disabled }) => (
  <button
    onMouseDown={e => {
      e.preventDefault();
      onClick();
    }}
    className={`p-2 rounded hover:bg-gray-200 disabled:opacity-50 ${active ? 'bg-gray-200' : ''}`}
    title={title}
    disabled={disabled}
  >
    {children}
  </button>
));

const Toolbar = memo(({
  editor,
  setShowVideoModal,
  setShowImageModal,
  setShowLinkModal,
  showVoiceButton = true
}) => {
  const [recentColors, setRecentColors] = useState([]);
  const [hexColor, setHexColor] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const mediaRecorder = useRef(null);

  const { handleVoiceRequest } = useAi();

  if (!editor) return null;

  const setTextColor = useCallback(
    color => {
      if (color === null) {
        editor.chain().focus().unsetColor().run();
      } else {
        editor.chain().focus().setColor(color).run();
        setRecentColors(prevColors => {
          const newColors = [color, ...prevColors.filter(c => c !== color)];
          return newColors.slice(0, 5);
        });
      }
    },
    [editor]
  );

  const handleHexInputChange = e => {
    const value = e.target.value;
    setHexColor(value);
    if (/^#[0-9A-F]{6}$/i.test(value)) {
      setTextColor(value);
    }
  };

  const toggleBold = useCallback(() => editor.chain().focus().toggleBold().run(), [editor]);
  const toggleItalic = useCallback(() => editor.chain().focus().toggleItalic().run(), [editor]);
  const toggleStrike = useCallback(() => editor.chain().focus().toggleStrike().run(), [editor]);
  const clearFormatting = useCallback(() => editor.chain().focus().unsetAllMarks().run(), [editor]);
  const setParagraph = useCallback(() => editor.chain().focus().setParagraph().run(), [editor]);
  const setAlignLeft = useCallback(() => editor.chain().focus().setTextAlign('left').run(), [editor]);
  const setAlignCenter = useCallback(() => editor.chain().focus().setTextAlign('center').run(), [editor]);
  const setAlignRight = useCallback(() => editor.chain().focus().setTextAlign('right').run(), [editor]);
  const toggleHeading = useCallback((level) => editor.chain().focus().toggleHeading({ level }).run(), [editor]);
  const toggleBulletList = useCallback(() => editor.chain().focus().toggleBulletList().run(), [editor]);
  const toggleOrderedList = useCallback(() => editor.chain().focus().toggleOrderedList().run(), [editor]);
  const toggleCodeBlock = useCallback(() => editor.chain().focus().toggleCodeBlock().run(), [editor]);
  const toggleBlockquote = useCallback(() => editor.chain().focus().toggleBlockquote().run(), [editor]);
  const setHorizontalRule = useCallback(() => editor.chain().focus().setHorizontalRule().run(), [editor]);
  const handleUndo = useCallback(() => editor.chain().focus().undo().run(), [editor]);
  const handleRedo = useCallback(() => editor.chain().focus().redo().run(), [editor]);

  const getCurrentAlignmentIcon = () => {
    if (editor.isActive({ textAlign: 'center' })) return <AlignCenter size={18} />;
    if (editor.isActive({ textAlign: 'right' })) return <AlignRight size={18} />;
    return <AlignLeft size={18} />;
  };

  const handleVoice = useCallback(async () => {
    if (isProcessing || !handleVoiceRequest) return;

    if (!isRecording) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        mediaRecorder.current = new MediaRecorder(stream);
        const audioChunks = [];

        mediaRecorder.current.ondataavailable = event => audioChunks.push(event.data);

        mediaRecorder.current.onstop = async () => {
          const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
          setIsRecording(false);
          stream.getTracks().forEach(track => track.stop());

          try {
            setIsProcessing(true);
            const text = await handleVoiceRequest(audioBlob);
            editor.chain().focus().insertContent(text).run();
          } catch (error) {
            console.error('Error processing voice request:', error);
          } finally {
            setIsProcessing(false);
          }
        };

        mediaRecorder.current.start();
        setIsRecording(true);
      } catch (error) {
        console.error('Error accessing microphone:', error);
        setIsRecording(false);
        setIsProcessing(false);
      }
    } else {
      if (mediaRecorder.current && mediaRecorder.current.state === 'recording') {
        mediaRecorder.current.stop();
      }
    }
  }, [isRecording, editor, isProcessing, handleVoiceRequest]);

  const colors = [
    { color: '#000000', name: 'Black' },
    { color: '#6B7280', name: 'Gray' },
    { color: '#EF4444', name: 'Red' },
    { color: '#F59E0B', name: 'Yellow' },
    { color: '#10B981', name: 'Green' },
    { color: '#3B82F6', name: 'Blue' },
    { color: '#6366F1', name: 'Indigo' },
    { color: '#8B5CF6', name: 'Purple' },
    { color: '#EC4899', name: 'Pink' },
    { color: '#14B8A6', name: 'Teal' },
    { color: '#F97316', name: 'Orange' },
    { color: '#84CC16', name: 'Lime' },
    { color: '#10B981', name: 'Emerald' },
    { color: '#06B6D4', name: 'Cyan' }
  ];

  return (
    <div className="flex flex-col shadow-sm border-b border-gray-200">
      <div className="flex flex-wrap gap-1 p-1">
        <ToolbarButton onClick={toggleBold} active={editor.isActive('bold')} title="Bold">
          <Bold size={18} />
        </ToolbarButton>
        <ToolbarButton onClick={toggleItalic} active={editor.isActive('italic')} title="Italic">
          <Italic size={18} />
        </ToolbarButton>
        <ToolbarButton onClick={toggleStrike} active={editor.isActive('strike')} title="Strikethrough">
          <Strikethrough size={18} />
        </ToolbarButton>

        {/* Color dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="p-2 rounded hover:bg-gray-200" title="Text Color">
              <Palette size={18} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-40 p-2">
            <div className="grid grid-cols-5 gap-1 mb-2">
              {colors.map(({ color, name }) => (
                <button
                  key={color}
                  className="w-6 h-6 rounded-full hover:ring-2 hover:ring-gray-300"
                  style={{ backgroundColor: color }}
                  onClick={() => setTextColor(color)}
                  aria-label={`Set text color to ${name}`}
                />
              ))}
            </div>
            <Input
              type="text"
              placeholder="#FFFFFF"
              value={hexColor}
              onChange={handleHexInputChange}
              className="h-8 text-sm"
            />
            {recentColors.length > 0 && (
              <>
                <div className="text-xs mt-2 mb-1 text-gray-500">Recent:</div>
                <div className="flex gap-1">
                  {recentColors.map((color, index) => (
                    <button
                      key={index}
                      className="w-6 h-6 rounded-full hover:ring-2 hover:ring-gray-300"
                      style={{ backgroundColor: color }}
                      onClick={() => setTextColor(color)}
                    />
                  ))}
                </div>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        <ToolbarButton onClick={clearFormatting} title="Clear Formatting">
          <Eraser size={18} />
        </ToolbarButton>

        {/* Heading dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="p-2 rounded hover:bg-gray-200" title="Text Styles">
              <Heading size={18} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-40">
            <DropdownMenuItem onSelect={setParagraph} className="flex items-center justify-between">
              <span className="flex items-center gap-2"><Type size={18} /> Paragraph</span>
              {editor.isActive('paragraph') && <Check size={16} />}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => toggleHeading(1)} className="flex items-center justify-between">
              <span className="flex items-center gap-2"><Heading1 size={18} /> Heading 1</span>
              {editor.isActive('heading', { level: 1 }) && <Check size={16} />}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => toggleHeading(2)} className="flex items-center justify-between">
              <span className="flex items-center gap-2"><Heading2 size={18} /> Heading 2</span>
              {editor.isActive('heading', { level: 2 }) && <Check size={16} />}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => toggleHeading(3)} className="flex items-center justify-between">
              <span className="flex items-center gap-2"><Heading3 size={18} /> Heading 3</span>
              {editor.isActive('heading', { level: 3 }) && <Check size={16} />}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => toggleHeading(4)} className="flex items-center justify-between">
              <span className="flex items-center gap-2"><Heading4 size={18} /> Heading 4</span>
              {editor.isActive('heading', { level: 4 }) && <Check size={16} />}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => toggleHeading(5)} className="flex items-center justify-between">
              <span className="flex items-center gap-2"><Heading5 size={18} /> Heading 5</span>
              {editor.isActive('heading', { level: 5 }) && <Check size={16} />}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => toggleHeading(6)} className="flex items-center justify-between">
              <span className="flex items-center gap-2"><Heading6 size={18} /> Heading 6</span>
              {editor.isActive('heading', { level: 6 }) && <Check size={16} />}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Alignment dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="p-2 rounded hover:bg-gray-200" title="Text Alignment">
              {getCurrentAlignmentIcon()}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-40">
            <DropdownMenuItem onSelect={setAlignLeft} className="flex items-center justify-between">
              <span className="flex items-center gap-2"><AlignLeft size={18} /> Align Left</span>
              {editor.isActive({ textAlign: 'left' }) && <Check size={16} />}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={setAlignCenter} className="flex items-center justify-between">
              <span className="flex items-center gap-2"><AlignCenter size={18} /> Align Center</span>
              {editor.isActive({ textAlign: 'center' }) && <Check size={16} />}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={setAlignRight} className="flex items-center justify-between">
              <span className="flex items-center gap-2"><AlignRight size={18} /> Align Right</span>
              {editor.isActive({ textAlign: 'right' }) && <Check size={16} />}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <ToolbarButton onClick={toggleBulletList} active={editor.isActive('bulletList')} title="Bullet List">
          <List size={18} />
        </ToolbarButton>
        <ToolbarButton onClick={toggleOrderedList} active={editor.isActive('orderedList')} title="Numbered List">
          <ListOrdered size={18} />
        </ToolbarButton>
        <ToolbarButton onClick={toggleCodeBlock} active={editor.isActive('codeBlock')} title="Code Block">
          <FileCode size={18} />
        </ToolbarButton>
        <ToolbarButton onClick={toggleBlockquote} active={editor.isActive('blockquote')} title="Block Quote">
          <Quote size={18} />
        </ToolbarButton>
        <ToolbarButton onClick={() => setShowLinkModal(true)} title="Insert Link">
          <LinkIcon size={18} />
        </ToolbarButton>
        <ToolbarButton onClick={setHorizontalRule} title="Horizontal Rule">
          <Minus size={18} />
        </ToolbarButton>
        <ToolbarButton onClick={() => setShowVideoModal(true)} title="Add Video">
          <VideoIcon size={18} />
        </ToolbarButton>
        <ToolbarButton onClick={() => setShowImageModal(true)} title="Add Image">
          <ImageIcon size={18} />
        </ToolbarButton>
        <ToolbarButton onClick={handleUndo} title="Undo">
          <Undo size={18} />
        </ToolbarButton>
        <ToolbarButton onClick={handleRedo} title="Redo">
          <Redo size={18} />
        </ToolbarButton>

        <AiMenu editor={editor} />

        {showVoiceButton && handleVoiceRequest && (
          <ToolbarButton
            onClick={handleVoice}
            active={isRecording}
            title="Voice"
            disabled={isProcessing}
          >
            {isProcessing ? (
              <Loader2 size={18} className="animate-spin" />
            ) : isRecording ? (
              <div className="w-4 h-4 rounded-full bg-red-500 animate-pulse" />
            ) : (
              <Mic size={18} />
            )}
          </ToolbarButton>
        )}
      </div>
    </div>
  );
});

export default Toolbar;
